/**
 * @file
 * GA4 tracking for the "Applicant Classification Form".
 *
 * Detects the visible classification result (NAO / GEO / CONTACT)
 * on the final step of the webform ("Your result") and pushes a
 * `classification_result` event to the global dataLayer for GA4 / GTM.
 *
 * On the ICHAS variant of the form, also forwards UTM query parameters
 * from the current page URL to any outbound myichas.ie links.
 *
 * Loaded for webforms: applicant_classification_form,
 * applicant_classification_ichas.
 */

(function (Drupal, once) {
  'use strict';

  /**
   * Checks whether the given element is visible on the page.
   *
   * Webform hides non-relevant results inside `.js-webform-states-hidden`
   * wrappers, so this helper ensures we only track the visible one.
   *
   * @param {HTMLElement} el
   *   The DOM element to check.
   *
   * @returns {boolean}
   *   TRUE if the element is visible.
   */
  function isVisibleResult(el) {
    if (!el) return false;
    if (el.closest('.js-webform-states-hidden')) return false;
    if (typeof el.offsetParent === 'undefined') return true; // Safety for detached nodes.
    return el.offsetParent !== null;
  }

  /**
   * Detects which result block (NAO / GEO / CONTACT) is currently visible.
   *
   * @param {HTMLElement} context
   *   The webform page context (Step 2: "Your result").
   *
   * @returns {string|null}
   *   The classification code (GEO, NAO, CONTACT) or null if none found.
   */
  function detectClassification(context) {
    var geo = context.querySelector('.app-result--geo');
    var nao = context.querySelector('.app-result--nao');
    var contact = context.querySelector('.app-result--contact');

    if (isVisibleResult(geo)) return 'GEO';
    if (isVisibleResult(nao)) return 'NAO';
    if (isVisibleResult(contact)) return 'CONTACT';
    return null;
  }

  /**
   * Appends the current page's query parameters to all myichas.ie links
   * within the given container.
   *
   * @param {HTMLElement} container
   *   The result step container to scan for links.
   */
  function forwardQueryParamsToIchasLinks(container) {
    var pageParams = new URLSearchParams(window.location.search);
    if (!pageParams.toString()) {
      return;
    }

    var links = container.querySelectorAll('a[href*="myichas.ie"]');
    for (var i = 0; i < links.length; i++) {
      var url = new URL(links[i].href);
      pageParams.forEach(function (value, key) {
        if (!url.searchParams.has(key)) {
          url.searchParams.set(key, value);
        }
      });
      links[i].href = url.toString();
    }
  }

  /**
   * Drupal behavior: Detect and send classification result to GA4.
   *
   * Executes once per page load, when the user reaches the "Your result" step.
   */
  Drupal.behaviors.applicantClassificationGA = {
    attach: function (context) {
      var pages = once('classification-ga', '[data-webform-key="your_result"]', context);
      if (!pages.length) {
        return;
      }

      var resultPage = pages[0];

      // Forward UTM parameters to myichas.ie links.
      forwardQueryParamsToIchasLinks(resultPage);

      var classification = detectClassification(resultPage);
      if (!classification) {
        return;
      }

      window.dataLayer = window.dataLayer || [];

      window.dataLayer.push({
        event: 'classification_result',
        classification: classification
      });
    }
  };

})(Drupal, once);
